import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, type TwoFAPendingState } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import styles from './AuthModal.module.css';

export type AuthMode = 'login' | 'register' | 'forgot-password' | '2fa-verify';

function validatePassword(password: string) {
  return {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
  };
}

type PolicyType = 'terms' | 'privacy' | 'cookies';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: AuthMode;
  /** Path to navigate to after successful login / 2FA verify. When omitted,
   *  defaults to "/". Used by the login landing's `?redirect=<url>` flow
   *  (e.g. user got bounced from /payment back to /login, we send them back
   *  to /payment after auth). */
  redirectTo?: string;
}

/** Self-contained login/register/forgot/2FA modal. The form was previously the
 *  whole LoginPage; now it lives here so the landing page can stay marketing-only
 *  and pop the modal on demand. All behaviour (2FA, honeypot, policy modals,
 *  validation, language toggle) is preserved 1:1. */
export function AuthModal({ isOpen, onClose, initialMode = 'login', redirectTo }: AuthModalProps) {
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [policyModal, setPolicyModal] = useState<PolicyType | null>(null);
  const [twoFAPending, setTwoFAPending] = useState<TwoFAPendingState | null>(null);
  const [twoFACode, setTwoFACode] = useState('');
  const { login, verify2FA, register, forgotPassword } = useAuth();
  const navigate = useNavigate();
  const { t, language, toggleLanguage } = useLanguage();

  // Reset mode when modal opens with a new initialMode
  useEffect(() => {
    if (isOpen) setMode(initialMode);
  }, [isOpen, initialMode]);

  // Lock body scroll while modal is open (avoids landing scrolling under
  // the overlay on iOS)
  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const openPolicy = (policy: PolicyType) => setPolicyModal(policy);
  const closePolicy = () => setPolicyModal(null);

  const passwordChecks = validatePassword(password);
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError('');
    setSuccessMessage('');
    setIsSubmitting(true);
    try {
      if (mode === 'login') {
        const result = await login(email, password);
        if (result) {
          setTwoFAPending(result);
          setMode('2fa-verify');
        } else {
          onClose();
          navigate(redirectTo || '/');
        }
      } else if (mode === '2fa-verify' && twoFAPending) {
        await verify2FA(twoFAPending.tempToken, twoFACode);
        onClose();
        navigate(redirectTo || '/');
      } else if (mode === 'register') {
        if (name.trim()) {
          // honeypot — pretend success to bots
          await new Promise(resolve => setTimeout(resolve, 1000));
          setSuccessMessage(t.auth.checkYourEmail);
          return;
        }
        if (!isPasswordValid) {
          setError(t.auth.passwordRequirements);
          setIsSubmitting(false);
          return;
        }
        if (password !== confirmPassword) {
          setError(t.auth.passwordsDoNotMatch);
          setIsSubmitting(false);
          return;
        }
        await register(email, password, '');
        setSuccessMessage(t.auth.checkYourEmail);
      } else if (mode === 'forgot-password') {
        await forgotPassword(email);
        setSuccessMessage(t.auth.resetLinkSent);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMode = () => {
    setMode(m => m === 'login' ? 'register' : 'login');
    setError('');
    setSuccessMessage('');
    setConfirmPassword('');
    setTwoFAPending(null);
    setTwoFACode('');
  };

  const goToForgotPassword = () => {
    setMode('forgot-password');
    setError('');
    setSuccessMessage('');
    setPassword('');
  };

  const backToLogin = () => {
    setMode('login');
    setError('');
    setSuccessMessage('');
    setTwoFAPending(null);
    setTwoFACode('');
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.card} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className={styles.closeBtn} aria-label="Kapat">&times;</button>
        <button onClick={toggleLanguage} className={styles.langBtn}>
          {language === 'tr' ? 'EN' : 'TR'}
        </button>

        <div className={styles.header}>
          <div className={styles.logo}>
            <img src="/polaris.svg" alt="Polaris" width="48" height="48" />
          </div>
          <h2 className={styles.title}>Polaris</h2>
          <p className={styles.subtitle}>
            {mode === 'login' && t.auth.signInToAccount}
            {mode === 'register' && t.auth.createAccount}
            {mode === 'forgot-password' && t.auth.forgotPasswordSubtitle}
            {mode === '2fa-verify' && (language === 'tr' ? 'İki faktörlü doğrulama' : 'Two-factor authentication')}
          </p>
        </div>

        {successMessage ? (
          <div className={styles.form}>
            <p className={styles.success}>{successMessage}</p>
            {mode !== 'login' && (
              <button onClick={backToLogin} className={styles.submitBtn}>
                {t.auth.backToLogin}
              </button>
            )}
          </div>
        ) : mode === '2fa-verify' ? (
          <form onSubmit={handleSubmit} className={styles.form}>
            <p className={styles.twoFAInfo}>
              {language === 'tr'
                ? 'Authenticator uygulamanızdaki 6 haneli kodu veya yedek kodlarınızdan birini girin.'
                : 'Enter the 6-digit code from your authenticator app or one of your backup codes.'}
            </p>
            <div className={styles.field}>
              <label htmlFor="twoFACode" className={styles.label}>
                {language === 'tr' ? 'Doğrulama Kodu' : 'Verification Code'}
              </label>
              <input
                id="twoFACode"
                type="text"
                value={twoFACode}
                onChange={e => setTwoFACode(e.target.value.replace(/[^0-9A-Za-z-]/g, ''))}
                className={styles.input}
                placeholder={language === 'tr' ? '6 haneli kod veya yedek kod' : '6-digit code or backup code'}
                required
                autoComplete="one-time-code"
                maxLength={12}
                autoFocus
              />
            </div>
            {error && <p className={styles.error}>{error}</p>}
            <button type="submit" className={styles.submitBtn} disabled={isSubmitting}>
              {isSubmitting
                ? (language === 'tr' ? 'Doğrulanıyor...' : 'Verifying...')
                : (language === 'tr' ? 'Doğrula' : 'Verify')}
            </button>
            <button type="button" onClick={backToLogin} className={styles.forgotBtn}>
              {t.auth.backToLogin}
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className={styles.form}>
            {mode === 'register' && (
              <div className={styles.honeypot} aria-hidden="true">
                <label htmlFor="name">{t.auth.name}</label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
            )}

            <div className={styles.field}>
              <label htmlFor="email" className={styles.label}>{t.auth.email}</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className={styles.input}
                placeholder={t.auth.enterEmail}
                required
                autoComplete="email"
              />
            </div>

            {mode !== 'forgot-password' && (
              <div className={styles.field}>
                <label htmlFor="password" className={styles.label}>{t.auth.password}</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className={styles.input}
                  placeholder={t.auth.enterPassword}
                  required
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                />
              </div>
            )}

            {mode === 'register' && (
              <>
                <div className={styles.field}>
                  <label htmlFor="confirmPassword" className={styles.label}>{t.auth.confirmPassword}</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    className={styles.input}
                    placeholder={t.auth.confirmYourPassword}
                    required
                    autoComplete="new-password"
                  />
                </div>
                {password && (
                  <div className={styles.passwordRequirements}>
                    <p className={styles.requirementsTitle}>{t.auth.passwordRequirements}</p>
                    <ul className={styles.requirementsList}>
                      <li className={passwordChecks.minLength ? styles.valid : styles.invalid}>{t.auth.passwordMinLength}</li>
                      <li className={passwordChecks.hasUppercase ? styles.valid : styles.invalid}>{t.auth.passwordUppercase}</li>
                      <li className={passwordChecks.hasLowercase ? styles.valid : styles.invalid}>{t.auth.passwordLowercase}</li>
                      <li className={passwordChecks.hasNumber ? styles.valid : styles.invalid}>{t.auth.passwordNumber}</li>
                    </ul>
                  </div>
                )}
              </>
            )}

            {error && <p className={styles.error}>{error}</p>}

            {mode === 'register' && (
              <div className={styles.termsNotice}>
                <p>
                  {language === 'tr' ? 'Kayıt olarak ' : 'By registering, you accept our '}
                  <button type="button" className={styles.policyLink} onClick={() => openPolicy('terms')}>{t.auth.termsOfUse}</button>
                  {language === 'tr' ? ', ' : ', '}
                  <button type="button" className={styles.policyLink} onClick={() => openPolicy('privacy')}>{t.auth.privacyPolicy}</button>
                  {language === 'tr' ? ' ve ' : ', and '}
                  <button type="button" className={styles.policyLink} onClick={() => openPolicy('cookies')}>{t.auth.cookiePolicy}</button>
                  {language === 'tr' ? ' kabul etmiş olursunuz.' : '.'}
                </p>
                <p className={styles.disclaimer}>{t.auth.softwareDisclaimer}</p>
              </div>
            )}

            <button type="submit" className={styles.submitBtn} disabled={isSubmitting}>
              {isSubmitting ? t.auth.pleaseWait : (
                mode === 'login' ? t.auth.signIn :
                mode === 'register' ? t.auth.acceptAndRegister :
                t.auth.sendResetLink
              )}
            </button>

            {mode === 'login' && (
              <button type="button" onClick={goToForgotPassword} className={styles.forgotBtn}>
                {t.auth.forgotPassword}
              </button>
            )}
          </form>
        )}

        <div className={styles.footer}>
          {mode === 'forgot-password' ? (
            <p><button onClick={backToLogin} className={styles.toggleBtn}>{t.auth.backToLogin}</button></p>
          ) : (
            <p>
              {mode === 'login' ? t.auth.noAccount : t.auth.hasAccount}
              <button onClick={toggleMode} className={styles.toggleBtn}>
                {mode === 'login' ? t.auth.signUp : t.auth.signIn}
              </button>
            </p>
          )}
        </div>

        {/* Inline policy modal (within AuthModal) */}
        {policyModal && (
          <div className={styles.policyOverlay} onClick={closePolicy}>
            <div className={styles.policyCard} onClick={(e) => e.stopPropagation()}>
              <div className={styles.policyHeader}>
                <h3>
                  {policyModal === 'terms' && t.auth.termsOfUseTitle}
                  {policyModal === 'privacy' && t.auth.privacyPolicyTitle}
                  {policyModal === 'cookies' && t.auth.cookiePolicyTitle}
                </h3>
                <button className={styles.policyClose} onClick={closePolicy}>&times;</button>
              </div>
              <div className={styles.policyBody}>
                {policyModal === 'terms' && <div dangerouslySetInnerHTML={{ __html: t.policies.terms }} />}
                {policyModal === 'privacy' && <div dangerouslySetInnerHTML={{ __html: t.policies.privacy }} />}
                {policyModal === 'cookies' && <div dangerouslySetInnerHTML={{ __html: t.policies.cookies }} />}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
